import React, { useState, useEffect, useRef } from 'react';
import qs from 'qs';
import produce from 'immer';
import axios from 'axios';
import { Dropdown, Loader } from 'semantic-ui-react';

import {
  Root,
  Tile,
  FormWrap,
  ButtonSection,
  SelectSearchersWrap,
  Chip,
  NotesInput,
  SelectPropertiesWrap,
} from './indexStyles';
import SearchDropdown from './SearchDropdown';
import { Close, XIcon, Plus } from './icons';
import { CategorySearch, Property, SearchType } from '../../types';
import useOnClickOutside from '../../OrderEntry/ui/outside-click';
interface Props {
  open: boolean;
  onClose: () => void;
  properties: Property[];
  clientName: string;
  orderId: number;
  userId: any;
  titleNumber: string;
}

interface AddSearchesErrors {
  search?: boolean;
  property?: boolean;
}

export default function AddSearchModal(props: Props) {
  const searchRef = useRef();
  const [searchDropdown, openSearchDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categorySearches, setCategorySearches] = useState<CategorySearch[]>([]);
  const [selectedSearches, setSelectedSearches] = useState<SearchType[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);
  const [errors, setErrors] = useState<AddSearchesErrors>({});
  const [note, setNote] = useState<string>('');

  const getCountyId = (countyId: number) => {
    if (!countyId) {
      return countyId;
    }
    return props.properties.every(
      property => property.countyId === countyId || property.countyId === countyId
    )
      ? countyId
      : null;
  };

  useEffect(() => {
    const countyId = (props.properties.length > 0 ? props.properties[0].countyId : {}) as any;
    var stringified = qs.stringify({ countyId: getCountyId(countyId) });
    axios
      .get<CategorySearch[]>(`/api/clientsearch/GetSearchCategoriesAndTypes?${stringified}`)
      .then(({ data }) => {
        setCategorySearches(data);
      });
    setSelectedProperties(props.properties.map(p => p.id));
  }, [JSON.stringify(props.properties)]);

  const onSelectSearch = (searchType: SearchType) => {
    const newSelectedSearches = produce(selectedSearches, draft => {
      draft.push(searchType);
    });
    setSelectedSearches(newSelectedSearches);
  };
  const onRemoveSearch = (index: number) => {
    const newSelectedSearches = produce(selectedSearches, draft => {
      draft.splice(index, 1);
    });
    setSelectedSearches(newSelectedSearches);
  };

  const onRemoveProperty = (index: number) => {
    const newProperties = produce(selectedProperties, draft => {
      draft.splice(index, 1);
    });
    setSelectedProperties(newProperties);
  };

  useOnClickOutside(searchRef, () => openSearchDropdown(false));

  const addSearchesToOrder = async () => {
    if (!selectedSearches.length || !selectedProperties.length) {
      const errors: AddSearchesErrors = {
        property: !selectedProperties.length,
        search: !selectedSearches.length,
      };
      setErrors(errors);
    } else {
      setErrors({});
      const objToPost = {
        searchTypes: selectedSearches,
        propertyIds: selectedProperties,
        note,
        clientName: props.clientName,
        orderId: props.orderId,
        userId: props.userId,
        titleNumber: props.titleNumber
      };
      setSubmitting(true);
      await axios.post(`/api/clientSearch/AddSearchesToOrder`, objToPost);
      setSubmitting(false);
      props.onClose();
    }
  };
  return (
    <Root open={props.open}>
      <Tile>
        <div className="header">
          <div>Add Search</div>
          <Close onClick={props.onClose} />
        </div>
        <FormWrap>
          <div className="label">Select Search</div>
          <SelectSearchersWrap>
            {selectedSearches.map((selectedSearch, i) => {
              return (
                <Chip key={selectedSearch.id}>
                  {selectedSearch.type} <XIcon onClick={() => onRemoveSearch(i)} />
                </Chip>
              );
            })}
            <div className="addSearch" onClick={() => openSearchDropdown(!searchDropdown)}>
              <Plus /> Add Search
            </div>
            <SearchDropdown
              isOpen={searchDropdown}
              searchRef={searchRef}
              categorySearches={categorySearches}
              onSelectSearch={onSelectSearch}
              selectedSearches={selectedSearches}
              closeDropdown={() => openSearchDropdown(false)}
            />
          </SelectSearchersWrap>
          {errors.search && <div style={{ color: 'red' }}>Please select a Search!</div>}
          {props.properties.length > 1 && (
            <>
              <div className="label">Properties</div>
              <SelectPropertiesWrap>
                {props.properties
                  .filter(p => selectedProperties.includes(p.id))
                  .map((p, index) => (
                    <Chip>
                      {`${p.address}, ${p.section ? p.section + ', ' : ''}${p.block ? p.block + ', ' : ''}${
                        p.lot ? p.lot : ''
                      }`}
                      <XIcon onClick={() => onRemoveProperty(index)} />
                    </Chip>
                  ))}
                {props.properties.length > selectedProperties.length && (
                  <Dropdown
                    placeholder="Add Property"
                    fluid
                    selection
                    value={selectedProperties}
                    multiple
                    onChange={(e, { value }) => {
                      setSelectedProperties(value as number[]);
                    }}
                    options={props.properties
                      .filter(p => !selectedProperties.includes(p.id))
                      .map(p => ({
                        key: p.id,
                        text: `${p.address}, ${p.section ? p.section + ', ' : ''}${
                          p.block ? p.block + ', ' : ''
                        }${p.lot ? p.lot : ''}`,
                        value: p.id,
                      }))}
                  />
                )}
              </SelectPropertiesWrap>
            </>
          )}
          {errors.property && <div style={{ color: 'red' }}>Please select a property!</div>}
          <div className="label">Note</div>
          <NotesInput onChange={e => setNote(e.target.value)} placeholder="Enter your notes here" />
        </FormWrap>
        <ButtonSection>
          <button onClick={addSearchesToOrder} disabled={submitting}>
            Add to Order {submitting ? <Loader inline size="small" active /> : null}
          </button>
        </ButtonSection>
      </Tile>
    </Root>
  );
}
